import { enroll } from '@api/selfServiceServices';
import type { AppError as AppErrorUss } from '@errorHandler/errors';
import { Button } from '@imprivata-cloud/components';
import {
  type AppError,
  EnrollEventTypes,
  EventKind,
  FeatureFlagsProvider,
  GlobalEventSources,
  useEventListener,
} from '@imprivata-cloud/core';
import { NotificationSystem } from '@imprivata-cloud/design-system';
import {
  DefaultFaceCaptureBanner,
  FaceCaptureEnroll,
  FaceProcessingStatus,
} from '@imprivata-cloud/enroll';
import { useCallback, useState } from 'react';
import type { SelfServiceLandingPageDataResponse, UserInfo } from '@/api/types';
import { useErrorContext } from '@/errorHandler/context/ErrorContext';

interface EnrollNotificationPayload {
  online: boolean;
  status: FaceProcessingStatus;
  username?: string;
}

interface EnrollFlowProps {
  userInfo: UserInfo | undefined;
  data: SelfServiceLandingPageDataResponse | undefined;
  onSuccessfulFaceEnrollment: () => Promise<undefined | AppError>;
}
export const EnrollFlow = ({ userInfo, onSuccessfulFaceEnrollment, data }: EnrollFlowProps) => {
  const { emitError } = useErrorContext();
  const [key, setKey] = useState(0);

  useEventListener(event => {
    if (
      event.kind === EventKind.APP &&
      (event.type === EnrollEventTypes.CAMERA_ERROR ||
        event.type === EnrollEventTypes.PERMISSION_REVOKED ||
        event.type === EnrollEventTypes.DEVICE_DISCONNECTED)
    ) {
      emitError(event.payload as unknown as AppErrorUss);
    }
  });

  const forceUpdateComponent = useCallback(() => {
    setKey(key => key + 1);
  }, []);

  return (
    <FeatureFlagsProvider flags={{ fitVideoFrame: true }}>
      <NotificationSystem
        renderCustomBanner={event => {
          const payload = event?.payload as EnrollNotificationPayload;
          return (
            <DefaultFaceCaptureBanner
              type={event?.level ? event.level : 'info'}
              title={event?.title}
              message={event?.message}
              online={payload?.online}
              status={payload?.status}
              username={payload?.username}
              overlayActions={
                <div>
                  {payload?.status === FaceProcessingStatus.FAILED && (
                    <Button
                      type="primary"
                      ghost
                      block
                      size="small"
                      disabled={!payload?.online}
                      label={'Retry'}
                      onClick={forceUpdateComponent}
                      autoFocus
                    />
                  )}
                </div>
              }
            />
          );
        }}
      />
      <GlobalEventSources />
      <FaceCaptureEnroll.Root
        key={key}
        username={data?.userInfo.username || userInfo?.userUpn || userInfo?.email || ''}
        reset={forceUpdateComponent}
        enroll={enroll}
        handleEnrollComplete={onSuccessfulFaceEnrollment}
        isSuccessHandlerAutomatic={true}
      >
        <FaceCaptureEnroll.Overlay>
          <FaceCaptureEnroll.Camera />
        </FaceCaptureEnroll.Overlay>
      </FaceCaptureEnroll.Root>
    </FeatureFlagsProvider>
  );
};
